import React, { useState } from "react";
import subjectImportService from "../../services/subjectImportService";

const COLUMN_HELP = [
  {
    name: "track",
    required: true,
    description:
      "Which profession the subject belongs to. Use IT for Informatik or MEDI for Mediamatik.",
  },
  {
    name: "code",
    required: false,
    description:
      "REQUIRED FOR IT & MEDI. The module or subject code, e.g. M117. Leave it empty and one is generated from the title.",
  },
  {
    name: "title",
    required: true,
    description:
      "The subject name shown to apprentices and trainers, e.g. Informatik und Netzwerke.",
  },
];

const SubjectImportModal = ({ isOpen, onClose, onImportSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [fileError, setFileError] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.name.toLowerCase().endsWith(".csv")) {
      setSelectedFile(file);
      setUploadResult(null);
      setFileError(null);
    } else {
      setSelectedFile(null);
      setFileError("Please select a CSV file.");
      event.target.value = "";
    }
  };

  // Built in the browser from the same column list the parser uses, so the
  // template can never drift from what the import accepts.
  const handleDownloadTemplate = () => {
    const csv = subjectImportService.buildTemplateCsv();
    // The BOM makes Excel open the file as UTF-8 instead of mangling umlauts.
    const blob = new Blob([`﻿${csv}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "subject-import-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setFileError(null);
    try {
      const result = await subjectImportService.importCsv(selectedFile);
      setUploadResult(result);

      // Refresh whenever anything may have landed, including a partial import.
      if ((result.success || result.importedCount > 0) && onImportSuccess) {
        onImportSuccess();
      }
    } catch (error) {
      console.error("Import error:", error);
      setUploadResult({
        success: false,
        message: error.response?.data?.message || error.message || "Import failed",
        errors: null,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setFileError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-brand-xl border border-pale-dawn w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-pale-dawn bg-gradient-to-r from-ice-white to-pale-dawn/30 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center shrink-0">
              <i className="fas fa-file-csv"></i>
            </span>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-grey-dusk">
                Import subjects
              </h2>
              <p className="text-sm text-slate-cloud">
                Add many subjects at once from a spreadsheet
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            aria-label="Close"
            className="shrink-0 w-9 h-9 rounded-xl border border-pale-dawn bg-white text-gray-400 hover:text-grey-dusk hover:bg-pale-dawn/40 transition-colors flex items-center justify-center"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Template + column explanation */}
          <div className="rounded-2xl border border-pale-dawn bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-grey-dusk flex items-center">
                  <i className="fas fa-table text-sunrise-red mr-2"></i>
                  File format
                </h3>
                <p className="text-xs text-slate-cloud mt-1">
                  One subject per row. Open the template in Excel, fill it in
                  and save it as CSV.
                </p>
              </div>

              <button
                onClick={handleDownloadTemplate}
                className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-sunrise-red/30 bg-sunrise-red/5 text-sunrise-red text-xs font-semibold hover:bg-sunrise-red/10 transition-colors"
              >
                <i className="fas fa-download"></i>
                Download template
              </button>
            </div>

            <ul className="divide-y divide-pale-dawn/70">
              {COLUMN_HELP.map((column) => (
                <li key={column.name} className="py-2 flex items-start gap-3">
                  <code className="shrink-0 px-2 py-0.5 rounded bg-ice-white border border-pale-dawn text-[11px] font-semibold text-grey-dusk">
                    {column.name}
                  </code>
                  <div className="min-w-0">
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-wide ${
                        column.required ? "text-sunrise-red" : "text-gray-400"
                      }`}
                    >
                      {column.required ? "Required" : "Optional"}
                    </span>
                    <p className="text-xs text-gray-600 leading-snug">
                      {column.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <p className="mt-3 text-[11px] text-gray-400">
              Any other columns are ignored. Subjects whose code already exists
              are skipped, so re-importing a file is safe.
            </p>
          </div>

          {/* File picker */}
          <div className="rounded-2xl border border-pale-dawn bg-white p-4">
            <h3 className="text-sm font-bold text-grey-dusk flex items-center mb-3">
              <i className="fas fa-upload text-sunrise-red mr-2"></i>
              Choose your file
            </h3>

            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              aria-label="Select CSV file"
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-sunrise-red/10 file:text-sunrise-red hover:file:bg-sunrise-red/20 file:cursor-pointer"
            />

            {fileError && (
              <p className="mt-2 text-xs text-red-600 flex items-center">
                <i className="fas fa-circle-exclamation mr-2"></i>
                {fileError}
              </p>
            )}

            {selectedFile && (
              <div className="mt-3 flex items-center gap-3 rounded-xl bg-ice-white border border-pale-dawn px-3 py-2">
                <i className="fas fa-file-csv text-sunrise-red"></i>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-grey-dusk truncate">
                    {selectedFile.name}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Result */}
          {uploadResult && (
            <div
              className={`rounded-2xl border p-4 ${
                uploadResult.success
                  ? "border-green-200 bg-green-50"
                  : "border-amber-200 bg-amber-50"
              }`}
            >
              <h3
                className={`text-sm font-bold mb-2 flex items-center ${
                  uploadResult.success ? "text-green-800" : "text-amber-800"
                }`}
              >
                <i
                  className={`fas ${
                    uploadResult.success
                      ? "fa-circle-check"
                      : "fa-triangle-exclamation"
                  } mr-2`}
                ></i>
                Import result
              </h3>

              <p
                className={`text-sm ${
                  uploadResult.success ? "text-green-700" : "text-amber-800"
                }`}
              >
                {uploadResult.message}
              </p>

              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                {uploadResult.processedRows != null && (
                  <span>Rows read: {uploadResult.processedRows}</span>
                )}
                {uploadResult.importedCount != null && (
                  <span>Imported: {uploadResult.importedCount}</span>
                )}
                {uploadResult.skippedCount > 0 && (
                  <span>Already existed: {uploadResult.skippedCount}</span>
                )}
              </div>

              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-red-700 mb-1">
                    These rows were not imported:
                  </p>
                  <ul className="text-xs text-red-600 list-disc list-inside max-h-32 overflow-y-auto space-y-0.5">
                    {uploadResult.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-pale-dawn bg-ice-white flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button onClick={handleClose} className="btn-brand-outline sm:w-auto w-full">
            Close
          </button>
          <button
            onClick={handleImport}
            disabled={!selectedFile || isUploading}
            className="sm:w-auto w-full px-5 py-2.5 rounded-lg bg-sunrise-red text-white text-sm font-semibold hover:bg-sunrise-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
          >
            {isUploading ? (
              <>
                <i className="fas fa-circle-notch fa-spin"></i>
                Importing...
              </>
            ) : (
              <>
                <i className="fas fa-file-import"></i>
                Import subjects
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubjectImportModal;
